import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles, Bot, Send, Image as ImageIcon, Camera, X,
  ChevronDown, RotateCcw, AlertCircle, BarChart2, CheckCircle2,
  Loader2, Trash2
} from 'lucide-react';
import { buildStudentContextSummary, sendKoçumAiMessage } from './geminiService';
import { saveStudentAiInteraction } from './aiLogService';

// Görseli tarayıcıda küçültüp optimize eden yardımcı (Gemini için yüksek hız)
const compressImageFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const maxWidth = 1200;
        const maxHeight = 1200;
        let { width, height } = img;

        if (width > height && width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        } else if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        const base64Data = dataUrl.split(',')[1];
        resolve({
          dataUrl,
          base64: base64Data,
          mimeType: 'image/jpeg'
        });
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

// Karmaşık LaTeX ve formül kalıntılarını temizleyip öğrenci için sade Türkçe geometri/matematik formatına çevirir
function sanitizeLatexAndMath(text) {
  if (!text) return '';
  return text
    // Açı ölçüleri: m(\widehat{ABC}) -> m(ABC)
    .replace(/m\s*\(\s*\\widehat\{([^}]+)\}\s*\)/g, 'm($1)')
    .replace(/\\widehat\{([^}]+)\}/g, '$1 açısı')
    // Kesirler: \frac{a}{b} -> (a / b)
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1 / $2)')
    // Karekök: \sqrt{64} -> √(64)
    .replace(/\\sqrt\{([^}]+)\}/g, '√($1)')
    // Text blokları: \text{ cm} -> cm
    .replace(/\\text\{([^}]+)\}/g, '$1')
    // Derece simgeleri: ^\circ veya \circ -> °
    .replace(/\^?\\circ/g, '°')
    // Çarpma, artı-eksi ve noktalar
    .replace(/\\cdot/g, '·')
    .replace(/\\times/g, '×')
    .replace(/\\pm/g, '±')
    // Eşitsizlikler
    .replace(/\\le(q)?\b/g, '≤')
    .replace(/\\ge(q)?\b/g, '≥')
    .replace(/\\ne(q)?\b/g, '≠')
    .replace(/\\approx/g, '≈')
    // Üslü sayılar: ^2 -> ², ^3 -> ³
    .replace(/\^2\b/g, '²')
    .replace(/\^3\b/g, '³')
    // Dolar işaretlerini kaldır ($...$ veya $$...$$)
    .replace(/\$+/g, '');
}

// Basit ve şık Markdown / Metin Biçimlendirici (Kazanım kutularını, başlıkları ve formülleri ayrıştırır)
const FormattedMessage = ({ content }) => {
  if (!content) return null;

  // Önce olası tüm LaTeX ve formül karmaşasını temizle
  const cleanedContent = sanitizeLatexAndMath(content);

  // Satırlara ayır ve özel blokları renklendir
  const lines = cleanedContent.split('\n');

  return (
    <div className="space-y-1.5 text-sm leading-relaxed">
      {lines.map((line, idx) => {
        const trimmed = line.trim();

        // Kazanım Analizi başlığı veya kutusu
        if (trimmed.startsWith('📌') || trimmed.includes('Kazanım Analizi')) {
          return (
            <div key={idx} className="mt-3 mb-1 p-2.5 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-300 font-semibold flex items-center gap-2">
              <span className="text-base">📌</span>
              <span>{trimmed.replace(/^📌\s*/, '')}</span>
            </div>
          );
        }

        // Kazanım detay maddeleri (- Ders:, - Ünite:, - Alt Konu:)
        if (trimmed.startsWith('- **Ders:**') || trimmed.startsWith('- **Ünite:**') || trimmed.startsWith('- **Konu / Kazanım:**') || trimmed.startsWith('- **Alt Konu:')) {
          return (
            <div key={idx} className="ml-2 pl-3 py-0.5 border-l-2 border-orange-500/40 text-xs sm:text-sm text-slate-200">
              <span dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(trimmed.replace(/^-\s*/, '')) }} />
            </div>
          );
        }

        // ### Başlıklar (Örn: ### Soru Çözümü:)
        if (trimmed.startsWith('### ')) {
          return (
            <h4 key={idx} className="mt-3 mb-1 text-sm font-bold text-orange-400 flex items-center gap-1.5">
              <span className="w-1.5 h-3.5 bg-orange-500 rounded-sm inline-block" />
              <span>{trimmed.replace(/^###\s*/, '')}</span>
            </h4>
          );
        }

        // ## Başlıklar
        if (trimmed.startsWith('## ')) {
          return (
            <h3 key={idx} className="mt-3 mb-1 text-base font-bold text-white">
              {trimmed.replace(/^##\s*/, '')}
            </h3>
          );
        }

        // Numaralandırılmış adımlar (Örn: 1. **İkizkenar Üçgen Özelliği:**)
        if (/^\d+\.\s+/.test(trimmed)) {
          return (
            <div key={idx} className="mt-3 mb-1 text-xs sm:text-sm font-bold text-amber-300">
              <span dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(trimmed) }} />
            </div>
          );
        }

        // Yatay çizgi
        if (trimmed === '---') {
          return <hr key={idx} className="my-2 border-slate-700/60" />;
        }

        // Liste maddesi (* veya -)
        if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
          return (
            <div key={idx} className="flex items-start gap-2 ml-1 text-slate-200">
              <span className="text-orange-400 mt-1 text-xs">•</span>
              <span className="flex-1" dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(trimmed.substring(2)) }} />
            </div>
          );
        }

        // Boş satır
        if (!trimmed) {
          return <div key={idx} className="h-1" />;
        }

        // Normal paragraf
        return (
          <p key={idx} className="text-slate-200" dangerouslySetInnerHTML={{ __html: formatInlineMarkdown(line) }} />
        );
      })}
    </div>
  );
};

// Inline Markdown (kalın **text**, kod `code`, dolar işaretleri vb.)
function formatInlineMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic text-slate-300">$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-slate-800 text-amber-300 px-1 py-0.5 rounded text-xs font-mono">$1</code>');
}

export default function KocumAiChat({ student, curriculum, user, db, isFirebaseActive }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null); // { dataUrl, base64, mimeType }
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [toastNotification, setToastNotification] = useState('');

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);

  const storageKey = `kocum_ai_history_${student?.id || 'guest'}`;

  // Geçmiş sohbetleri yükle
  useEffect(() => {
    if (!student?.id) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      }
    } catch (e) {
      console.warn("Sohbet geçmişi yüklenemedi:", e);
    }
  }, [student?.id, storageKey]);

  // Sohbeti localStorage'a kaydet
  useEffect(() => {
    if (!student?.id) return;
    try {
      if (messages.length > 0) {
        // Görsel verilerini localStorage boyutunu şişirmemek için sadeleştirip saklayalım
        const sanitized = messages.map(m => ({
          role: m.role,
          text: m.text,
          hasImage: !!m.imagePreview,
          imagePreview: m.imagePreview ? m.imagePreview.slice(0, 500) + '...' : null, // hafifletilmiş
          timestamp: m.timestamp || new Date().toISOString()
        }));
        localStorage.setItem(storageKey, JSON.stringify(sanitized));
      }
    } catch (e) {
      console.warn("Sohbet kaydedilemedi:", e);
    }
  }, [messages, student?.id, storageKey]);

  // Otomatik aşağı kaydırma
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setHasUnread(false);
    }
  }, [messages, isOpen, isLoading]);

  // Yeni Sohbet Tetikleme (Estetik Modal Açar)
  const handleResetClick = () => {
    if (messages.length > 0) {
      setShowConfirmModal(true);
    } else {
      executeResetChat();
    }
  };

  // Yeni Sohbeti Fiilen Başlat (Temizle)
  const executeResetChat = () => {
    setMessages([]);
    setSelectedImage(null);
    setInputText('');
    setShowConfirmModal(false);
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {}
  };

  // Görsel seçimi
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImageFile(file);
      setSelectedImage(compressed);
    } catch (err) {
      console.error("Görsel işlenirken hata:", err);
      setToastNotification("Görsel yüklenirken bir sorun oluştu.");
      setTimeout(() => setToastNotification(''), 3500);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Mesaj gönderme
  const handleSendMessage = async (textToSend = inputText) => {
    const text = (textToSend || '').trim();
    if (!text && !selectedImage) return;
    if (isLoading) return;

    const userMsg = {
      role: 'user',
      text: text,
      imagePreview: selectedImage?.dataUrl || null,
      timestamp: new Date().toISOString()
    };

    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputText('');
    const currentImg = selectedImage;
    setSelectedImage(null);
    setIsLoading(true);

    try {
      // Öğrencinin sistemdeki güncel veri özetini oluştur
      const studentContext = buildStudentContextSummary(student, curriculum);

      // Gemini için geçmiş hazırla (son 8 mesajı alalım ki token tasarrufu sağlansın)
      const apiHistory = newMessages.slice(-8).map(m => ({
        role: m.role,
        text: m.text,
        image: m === userMsg && currentImg ? currentImg.base64 : undefined,
        mimeType: m === userMsg && currentImg ? currentImg.mimeType : undefined
      }));

      const reply = await sendKoçumAiMessage(apiHistory, studentContext);

      const assistantMsg = {
        role: 'assistant',
        text: reply,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMsg]);
      if (!isOpen) setHasUnread(true);

      // Süperadmin için AI kullanımını logla
      saveStudentAiInteraction({
        student,
        userPrompt: text || (currentImg ? 'Soru Fotoğrafı Yüklendi' : ''),
        aiResponse: reply,
        hasImage: !!currentImg,
        imagePreview: currentImg?.dataUrl || null,
        db,
        isFirebaseActive
      });
    } catch (error) {
      console.error("Koçum AI yanıt hatası:", error);
      const errMsg = {
        role: 'assistant',
        text: "Üzgünüm, şu anda yanıt oluştururken bir bağlantı hatası oluştu. Lütfen internet bağlantınızı kontrol edip tekrar deneyiniz.",
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Hızlı öneri butonuna tıklandığında
  const handleQuickPrompt = (promptText) => {
    setInputText(promptText);
    handleSendMessage(promptText);
  };

  return (
    <>
      {/* 1. SAĞ ALT KÖŞE: YÜZEN SOHBET BALONU (FAB) */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center">
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="group relative flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500 text-white rounded-full shadow-2xl shadow-orange-500/40 hover:shadow-orange-500/60 hover:scale-105 active:scale-95 transition-all duration-300 border border-orange-400/40"
            title="Koçum Online AI ile Sohbet Et"
          >
            <div className="relative">
              <Bot size={22} className="animate-pulse" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-white animate-ping" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-white" />
            </div>
            <span className="font-bold text-sm tracking-wide hidden sm:inline">Koçum Online AI</span>

            {hasUnread && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full ring-2 ring-white animate-bounce" />
            )}
          </button>
        )}
      </div>

      {/* 2. SOHBET PENCERESİ */}
      {isOpen && (
        <div className="fixed bottom-4 right-3 sm:bottom-6 sm:right-6 w-[94vw] sm:w-[440px] h-[600px] max-h-[85vh] z-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-700/80 bg-slate-900 text-slate-100 backdrop-blur-xl animate-fade-in">
          
          {/* HEADER */}
          <div className="px-4 py-3 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700/80 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white shadow-md">
                <Bot size={20} />
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-slate-900" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                  Koçum Online AI
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 font-semibold border border-orange-500/30">
                    Akıllı Asistan
                  </span>
                </h3>
                <p className="text-[11px] text-slate-400">Soru Çözüm & İstatistik Rehberi</p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {/* Yeni Sohbet Butonu */}
              <button
                onClick={handleResetClick}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Yeni Sohbet (Tüm geçmişi sil)"
              >
                <RotateCcw size={16} />
              </button>

              {/* Kapat / Küçült Butonu */}
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Pencereyi Kapat"
              >
                <ChevronDown size={18} />
              </button>
            </div>
          </div>

          {/* SOHBET İÇERİK ALANI */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-slate-950/60">
            {messages.length === 0 ? (
              // KARŞILAMA VE HIZLI BAŞLANGIÇ KARTI
              <div className="space-y-4 pt-2">
                <div className="p-4 rounded-xl bg-gradient-to-b from-slate-800/80 to-slate-900/80 border border-slate-700/60 text-center space-y-2.5 shadow-sm">
                  <div className="w-12 h-12 rounded-2xl bg-orange-500/10 text-orange-400 flex items-center justify-center mx-auto border border-orange-500/20">
                    <Sparkles size={24} />
                  </div>
                  <h4 className="font-bold text-white text-sm">Merhaba {student?.name || ''}! 👋</h4>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Ben senin <b>Koçum Online AI</b> asistanınım. Takıldığın soruları bana sorabilir, fotoğraflarını yükleyebilir ve sistemdeki çalışma istatistiklerini öğrenebilirsin.
                  </p>
                  <div className="pt-2 text-[11px] text-amber-400/90 bg-amber-500/10 p-2.5 rounded-lg border border-amber-500/20 text-left flex items-start gap-2">
                    <AlertCircle size={15} className="mt-0.5 shrink-0 text-amber-400" />
                    <span>
                      <b>Not:</b> Çalışma programı ve koçluk planlaması için lütfen koçunuz <b>Uzm. Psk. Dan. Hamza Metehan KILIÇ</b> ile iletişime geçiniz.
                    </span>
                  </div>
                </div>

                {/* Hızlı Butonlar */}
                <div className="space-y-2">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block px-1">
                    Hızlı Sorular:
                  </span>
                  <div className="grid grid-cols-1 gap-2">
                    <button
                      onClick={() => handleQuickPrompt("Bu ay kaç soru çözdüm? Detaylı açıklar mısın?")}
                      className="p-2.5 text-left rounded-xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700 text-xs text-slate-200 hover:text-white flex items-center gap-2.5 transition-all group"
                    >
                      <BarChart2 size={16} className="text-orange-400 group-hover:scale-110 transition-transform" />
                      <span>Bu ay kaç soru çözdüm?</span>
                    </button>

                    <button
                      onClick={() => handleQuickPrompt("Son deneme sınavı netlerimi ve genel durumumu özetler misin?")}
                      className="p-2.5 text-left rounded-xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700 text-xs text-slate-200 hover:text-white flex items-center gap-2.5 transition-all group"
                    >
                      <CheckCircle2 size={16} className="text-emerald-400 group-hover:scale-110 transition-transform" />
                      <span>Son deneme netlerim nasıl?</span>
                    </button>

                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2.5 text-left rounded-xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700 text-xs text-slate-200 hover:text-white flex items-center gap-2.5 transition-all group"
                    >
                      <Camera size={16} className="text-blue-400 group-hover:scale-110 transition-transform" />
                      <span>Soru fotoğrafı yükle ve kazanımını öğren</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // MESAJ BALONLARI
              messages.map((msg, index) => {
                const isUser = msg.role === 'user';
                return (
                  <div
                    key={index}
                    className={`flex gap-2.5 ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}
                  >
                    {!isUser && (
                      <div className="w-7 h-7 rounded-lg bg-orange-600 flex items-center justify-center text-white shrink-0 mt-1 shadow-sm">
                        <Bot size={15} />
                      </div>
                    )}

                    <div
                      className={`max-w-[85%] rounded-2xl p-3.5 shadow-sm text-sm ${
                        isUser
                          ? 'bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-br-xs'
                          : msg.isError
                          ? 'bg-red-950/40 border border-red-500/30 text-red-200 rounded-bl-xs'
                          : 'bg-slate-800/90 border border-slate-700/60 text-slate-100 rounded-bl-xs'
                      }`}
                    >
                      {/* Kullanıcı görseli yüklediyse */}
                      {msg.imagePreview && (
                        <div className="mb-2 overflow-hidden rounded-lg border border-white/20 max-h-56">
                          <img
                            src={msg.imagePreview}
                            alt="Yüklenen Soru"
                            className="w-full object-cover"
                          />
                        </div>
                      )}

                      {/* Mesaj İçeriği */}
                      {isUser ? (
                        <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                      ) : (
                        <FormattedMessage content={msg.text} />
                      )}

                      {/* Zaman Damgası */}
                      <div className={`text-[10px] mt-1.5 text-right ${isUser ? 'text-white/70' : 'text-slate-400'}`}>
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* YÜKLENİYOR (YAZIYOR...) ANİMASYONU */}
            {isLoading && (
              <div className="flex gap-2.5 justify-start animate-fade-in">
                <div className="w-7 h-7 rounded-lg bg-orange-600 flex items-center justify-center text-white shrink-0 mt-1">
                  <Bot size={15} />
                </div>
                <div className="p-3.5 rounded-2xl rounded-bl-xs bg-slate-800/90 border border-slate-700/60 flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-orange-400" />
                  <span className="text-xs text-slate-300 font-medium">Koçum AI soruyu inceliyor...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* SEÇİLİ GÖRSEL ÖNİZLEME BARI */}
          {selectedImage && (
            <div className="px-3 py-2 bg-slate-800/90 border-t border-slate-700/60 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img
                  src={selectedImage.dataUrl}
                  alt="Önizleme"
                  className="w-10 h-10 object-cover rounded-md border border-slate-600"
                />
                <span className="text-xs text-slate-300">Soru fotoğrafı seçildi</span>
              </div>
              <button
                onClick={() => setSelectedImage(null)}
                className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-slate-700"
                title="Görseli Kaldır"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* INPUT ALANI */}
          <div className="p-3 bg-slate-900 border-t border-slate-700/80">
            {student?.aiBlocked ? (
              <div className="p-3 rounded-xl bg-red-950/50 border border-red-500/40 text-center text-xs text-red-200 space-y-1">
                <div className="font-bold text-red-400 flex items-center justify-center gap-1.5">
                  <AlertCircle size={15} />
                  <span>Yapay Zeka Erişimi Kısıtlandı</span>
                </div>
                <p className="text-[11px] text-slate-300">
                  Bu hesap için yapay zeka kullanımı yönetici tarafından geçici olarak duraklatılmıştır. Sorularınız için koçunuzla iletişime geçiniz.
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-slate-800/90 border border-slate-700 rounded-xl px-2.5 py-1.5 focus-within:border-orange-500 transition-colors">
                
                {/* Fotoğraf Yükleme Butonu */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-orange-400 hover:bg-slate-700/50 transition-colors shrink-0"
                  title="Soru Fotoğrafı Yükle"
                  disabled={isLoading}
                >
                  <ImageIcon size={19} />
                </button>

                {/* Metin Girişi */}
                <input
                  ref={textInputRef}
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={selectedImage ? "Soruyla ilgili notunuzu yazın..." : "Sorunu yaz veya soru fotoğrafı at..."}
                  disabled={isLoading}
                  className="flex-1 bg-transparent text-xs sm:text-sm text-slate-100 placeholder-slate-400 focus:outline-none py-1"
                />

                {/* Gönder Butonu */}
                <button
                  type="button"
                  onClick={() => handleSendMessage()}
                  disabled={isLoading || (!inputText.trim() && !selectedImage)}
                  className="p-2 rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:hover:bg-orange-600 text-white transition-all shrink-0"
                  title="Gönder"
                >
                  <Send size={15} />
                </button>
              </div>
            )}

            <div className="mt-1.5 text-[10px] text-center text-slate-400 truncate">
              Koçluk & program talepleri için: <span className="text-slate-300 font-semibold">Uzm. Psk. Dan. Hamza Metehan KILIÇ</span>
            </div>
          </div>

          {/* BİLDİRİM TOAST BARI */}
          {toastNotification && (
            <div className="absolute top-16 left-4 right-4 bg-amber-500/90 text-slate-950 font-bold px-3 py-2 rounded-xl text-xs flex items-center justify-between shadow-lg animate-fade-in z-20">
              <div className="flex items-center gap-2">
                <AlertCircle size={15} />
                <span>{toastNotification}</span>
              </div>
              <button onClick={() => setToastNotification('')} className="p-1 hover:opacity-80">
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* 3. ÖZEL ESTETİK YENİ SOHBET ONAY MODALI */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700/80 rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4 text-center animate-scale-in">
            {/* İkon */}
            <div className="w-12 h-12 rounded-2xl bg-orange-500/15 text-orange-400 flex items-center justify-center mx-auto border border-orange-500/30 shadow-inner">
              <RotateCcw size={22} className="animate-spin-slow" />
            </div>

            {/* Metinler */}
            <div className="space-y-1.5">
              <h3 className="text-base font-bold text-white tracking-wide">
                Yeni Sohbet Başlatılsın mı?
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed px-2">
                Tüm sohbet geçmişiniz temizlenecek ve yapay zeka ile sıfırdan yeni bir konuşma açılacaktır.
              </p>
            </div>

            {/* Butonlar */}
            <div className="flex items-center gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2.5 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs sm:text-sm font-semibold transition-all border border-slate-700 hover:border-slate-600"
              >
                Vazgeç
              </button>

              <button
                type="button"
                onClick={executeResetChat}
                className="flex-1 py-2.5 px-4 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-orange-500/25 transition-all flex items-center justify-center gap-1.5"
              >
                <Trash2 size={15} />
                <span>Evet, Temizle</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
